import React, { useState, useRef } from 'react';
import { X, Mic, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const CreateNoteModal = ({ isOpen, onClose, onSubmit, onComplete, projects = [] }) => {
  const [step, setStep] = useState('setup'); // 'setup', 'recording', 'processing', 'review'
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [transcript, setTranscript] = useState('');
  const [summary, setSummary] = useState('');
  
  // Form data
  const [meetingDate, setMeetingDate] = useState(new Date().toISOString().split('T')[0]);
  const [meetingTime, setMeetingTime] = useState('');
  const [title, setTitle] = useState('');
  const [projectId, setProjectId] = useState('');
  const [createTimeEntry, setCreateTimeEntry] = useState(false);
  const [duration, setDuration] = useState('');
  const [tags, setTags] = useState([]);
  
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [createdNoteId, setCreatedNoteId] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const MAX_RECORDING_TIME = 300; // 5 minutes

  // Start recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Try to create MediaRecorder with different mime types
      let mimeType = 'audio/webm';
      if (!MediaRecorder.isTypeSupported('audio/webm')) {
        if (MediaRecorder.isTypeSupported('audio/mp4')) {
          mimeType = 'audio/mp4';
        } else if (MediaRecorder.isTypeSupported('audio/ogg')) {
          mimeType = 'audio/ogg';
        } else {
          mimeType = '';
        }
      }
      
      const options = mimeType ? { mimeType } : {};
      mediaRecorderRef.current = new MediaRecorder(stream, options);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: mimeType || 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
        // Process audio after blob is set
        setTimeout(() => {
          if (audioChunksRef.current.length > 0) {
            processAudio(blob);
          }
        }, 100);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setStep('recording');
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          if (prev >= MAX_RECORDING_TIME) {
            stopRecording();
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (err) {
      setError('Could not access microphone. Please check permissions.');
      console.error('Recording error:', err);
      toast.error('Could not access microphone');
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
      // Don't call processAudio here - it will be called in onstop handler
    }
  };

  // Process audio (send to backend)
  const processAudio = async (blob) => {
    if (!blob) {
      // Wait for blob to be set
      setTimeout(() => {
        if (audioChunksRef.current.length > 0) {
          const mimeType = mediaRecorderRef.current?.mimeType || 'audio/webm';
          const newBlob = new Blob(audioChunksRef.current, { type: mimeType });
          setAudioBlob(newBlob);
          processAudio(newBlob);
        }
      }, 100);
      return;
    }

    setStep('processing');
    setLoading(true);
    setError('');

    try {
      const formData = {
        meeting_date: meetingDate,
        meeting_time: meetingTime || undefined,
        project_id: projectId || undefined,
        title: title || undefined
      };

      const response = await onSubmit(blob, formData);
      
      // Extract transcript and summary from response (backend returns Note object directly)
      if (response) {
        setTranscript(response.transcript || '');
        setSummary(response.summary || '');
        // Update title if auto-generated
        if (response.title && !title) {
          setTitle(response.title);
        }
        // Store note ID for updates
        if (response.id) {
          setCreatedNoteId(response.id);
        }
      }
      setStep('review');
    } catch (err) {
      setError(err.message || 'Failed to process recording');
      setStep('setup');
      toast.error(err.message || 'Failed to process recording');
    } finally {
      setLoading(false);
    }
  };

  // Final submit - update note if needed, link time entry if requested
  const handleFinalSubmit = async () => {
    if (!createdNoteId) {
      handleClose();
      return;
    }

    setLoading(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const API = `${API_URL}/api`;
      const token = localStorage.getItem('token');

      // Update note title and summary if changed
      const updateData = {};
      if (title) {
        updateData.title = title;
      }
      if (summary) {
        updateData.summary = summary;
      }
      
      if (Object.keys(updateData).length > 0) {
        try {
          await axios.put(
            `${API}/notes/${createdNoteId}`,
            updateData,
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );
        } catch (err) {
          console.error('Error updating note:', err);
          // Don't fail if update fails
        }
      }

      // Link time entry if requested
      if (createTimeEntry && duration) {
        try {
          const durationMinutes = Math.round(parseFloat(duration) * 60);
          if (durationMinutes > 0) {
            const payload = {
              duration_minutes: durationMinutes
            };
            
            // Only include project_id if it's not empty
            if (projectId && projectId.trim() !== '') {
              payload.project_id = projectId;
            }

            await axios.post(
              `${API}/notes/${createdNoteId}/link-time-entry`,
              payload,
              {
                headers: { 
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              }
            );
            toast.success('Time entry linked to note!');
            // Trigger refresh of time entries on home and calendar pages
            window.dispatchEvent(new CustomEvent('timeEntriesUpdated'));
          }
        } catch (err) {
          console.error('Error linking time entry:', err);
          const errorMessage = err.response?.data?.detail || err.response?.data?.message || 'Failed to link time entry';
          toast.error(errorMessage);
        }
      }

      // Notify parent to refresh notes list
      if (onComplete) {
        await onComplete();
      }

      handleClose();
    } catch (err) {
      setError(err.message || 'Failed to save note');
      toast.error(err.message || 'Failed to save note');
    } finally {
      setLoading(false);
    }
  };

  // Discard note - delete if created, then close
  const handleDiscard = async () => {
    if (createdNoteId) {
      // Delete the note that was created
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const API = `${API_URL}/api`;
        const token = localStorage.getItem('token');
        
        await axios.delete(`${API}/notes/${createdNoteId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        toast.success('Note discarded');
        
        // Notify parent to refresh notes list immediately
        if (onComplete) {
          await onComplete();
        }
      } catch (err) {
        console.error('Error deleting note:', err);
        toast.error('Failed to discard note');
      }
    }
    
    handleClose();
  };

  // Reset and close
  const handleClose = () => {
    setStep('setup');
    setIsRecording(false);
    setRecordingTime(0);
    setAudioBlob(null);
    setTranscript('');
    setSummary('');
    setMeetingDate(new Date().toISOString().split('T')[0]);
    setMeetingTime('');
    setTitle('');
    setProjectId('');
    setCreateTimeEntry(false);
    setDuration('');
    setTags([]);
    setError('');
    setCreatedNoteId(null);
    clearInterval(timerRef.current);
    
    // Stop recording if active
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    
    onClose();
  };

  // Format time for display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get date options
  const getDateOptions = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const twoDaysAgo = new Date(today);
    twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);

    return {
      today: today.toISOString().split('T')[0],
      yesterday: yesterday.toISOString().split('T')[0],
      twoDaysAgo: twoDaysAgo.toISOString().split('T')[0]
    };
  };

  const dates = getDateOptions();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={handleClose}
        aria-hidden="true"
      />
      
      {/* Container - Centered */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        {/* Modal - Centered */}
        <div className="relative bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col border border-gray-200 dark:border-gray-700 transition-colors">
          {/* Header - Fixed */}
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 flex-shrink-0 border-b border-gray-200 dark:border-gray-700 transition-colors">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 transition-colors">
                {step === 'setup' && '📝 Record New Note'}
                {step === 'recording' && '🎤 Recording...'}
                {step === 'processing' && '✨ Processing...'}
                {step === 'review' && '📝 Review Your Note'}
              </h3>
              <button
                onClick={handleClose}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-300 min-w-[44px] min-h-[44px] flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Content - Scrollable */}
          <div className="px-4 pt-4 pb-4 sm:px-6 sm:pt-6 sm:pb-6 overflow-y-auto flex-1 min-h-0 bg-white dark:bg-gray-800 transition-colors">
            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md transition-colors">
                <p className="text-sm text-red-600 dark:text-red-300 transition-colors">{error}</p>
              </div>
            )}

            {/* STEP 1: Setup */}
            {step === 'setup' && (
              <div className="space-y-4">
                {/* Date/Time Picker */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">
                    When did this happen?
                  </label>
                  
                  {/* Quick Select Buttons */}
                  <div className="flex gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => setMeetingDate(dates.today)}
                      className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                        meetingDate === dates.today
                          ? 'bg-blue-500 text-white border-blue-500'
                          : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                      }`}
                    >
                      Today
                    </button>
                    <button
                      type="button"
                      onClick={() => setMeetingDate(dates.yesterday)}
                      className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                        meetingDate === dates.yesterday
                          ? 'bg-blue-500 text-white border-blue-500'
                          : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                      }`}
                    >
                      Yesterday
                    </button>
                    <button
                      type="button"
                      onClick={() => setMeetingDate(dates.twoDaysAgo)}
                      className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                        meetingDate === dates.twoDaysAgo
                          ? 'bg-blue-500 text-white border-blue-500'
                          : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                      }`}
                    >
                      2 Days Ago
                    </button>
                  </div>

                  {/* Date Picker */}
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1 transition-colors">Date</label>
                      <Input
                        type="date"
                        value={meetingDate}
                        onChange={(e) => setMeetingDate(e.target.value)}
                        max={new Date().toISOString().split('T')[0]}
                        className="w-full bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 transition-colors"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1 transition-colors">Time (optional)</label>
                      <Input
                        type="time"
                        value={meetingTime}
                        onChange={(e) => setMeetingTime(e.target.value)}
                        className="w-full bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 transition-colors"
                      />
                    </div>
                  </div>
                </div>

                {/* Optional Title */}
                <div>
                  <Label>Note Title (optional)</Label>
                  <Input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g., Client Meeting, Team Standup"
                    className="mt-1"
                  />
                </div>

                {/* Project Selection */}
                {projects.length > 0 && (
                  <div>
                    <Label>Link to Project (optional)</Label>
                    <select
                      value={projectId}
                      onChange={(e) => setProjectId(e.target.value)}
                      className="w-full mt-1 px-3 py-2 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 focus:border-cyan-500 dark:focus:border-cyan-400 transition-colors font-medium"
                    >
                      <option value="">No project</option>
                      {projects.map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Recording Button */}
                <div className="mt-6">
                  <Button
                    onClick={startRecording}
                    className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-semibold py-6"
                  >
                    <Mic className="w-5 h-5 mr-2" />
                    Tap to Start Recording
                  </Button>
                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-2 transition-colors">
                    Max duration: 5 minutes
                  </p>
                </div>

                <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800 transition-colors">
                  <p className="text-sm text-blue-800 dark:text-blue-300 transition-colors">
                    💡 Tip: Speak clearly about what happened in your meeting. 
                    AI will automatically create a summary!
                  </p>
                </div>
              </div>
            )}

            {/* STEP 2: Recording */}
            {step === 'recording' && (
              <div className="text-center py-8">
                <div className="mb-6">
                  <div className="inline-block p-4 bg-red-500 rounded-full animate-pulse">
                    <Mic className="w-8 h-8 text-white" />
                  </div>
                </div>
                <div className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2 transition-colors">
                  {formatTime(recordingTime)} / 5:00
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-6 transition-colors">
                  <div 
                    className="bg-red-500 h-2 rounded-full transition-all"
                    style={{ width: `${(recordingTime / MAX_RECORDING_TIME) * 100}%` }}
                  />
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-6 transition-colors">
                  Recording for: {new Date(meetingDate).toLocaleDateString()}
                  {meetingTime && ` at ${meetingTime}`}
                </p>
                <Button
                  onClick={stopRecording}
                  className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium"
                >
                  ⏹️ Stop Recording
                </Button>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-4 transition-colors">
                  Speak naturally - AI will summarize your notes
                </p>
              </div>
            )}

            {/* STEP 3: Processing */}
            {step === 'processing' && (
              <div className="text-center py-8">
                <Loader2 className="w-16 h-16 mx-auto mb-4 text-cyan-500 dark:text-cyan-400 animate-spin transition-colors" />
                <p className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2 transition-colors">
                  Processing Your Note...
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 transition-colors">
                  Transcribing and summarizing...
                </p>
              </div>
            )}

            {/* STEP 4: Review */}
            {step === 'review' && (
              <div className="space-y-4">
                {/* Edit Title */}
                <div>
                  <Label>Note Title</Label>
                  <Input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Meeting Note"
                    className="mt-1"
                  />
                </div>

                {/* Full Transcript */}
                <div>
                  <Label className="text-gray-700 dark:text-gray-300 transition-colors">Your Full Note</Label>
                  <div className="mt-1 p-3 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-md max-h-40 overflow-y-auto transition-colors">
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap transition-colors">
                      {transcript}
                    </p>
                  </div>
                </div>

                {/* AI Summary - Editable */}
                <div>
                  <Label className="text-gray-700 dark:text-gray-300 transition-colors">✨ AI Summary</Label>
                  <Textarea
                    value={summary}
                    onChange={(e) => setSummary(e.target.value)}
                    placeholder="AI summary will appear here..."
                    className="mt-1 min-h-[120px] resize-y bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 transition-colors"
                    rows={5}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 transition-colors">
                    You can edit the AI-generated summary before saving
                  </p>
                </div>

                {/* Create Time Entry Option */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 transition-colors">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={createTimeEntry}
                      onChange={(e) => setCreateTimeEntry(e.target.checked)}
                      className="w-5 h-5 text-cyan-600 dark:text-cyan-500 rounded border-2 border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 focus:ring-offset-0 transition-colors cursor-pointer"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">
                      Also create time entry for this?
                    </span>
                  </label>
                  {createTimeEntry && (
                    <div className="mt-3 ml-6">
                      <Label className="text-gray-700 dark:text-gray-300 transition-colors">Duration (hours)</Label>
                      <Input
                        type="number"
                        step="0.25"
                        value={duration}
                        onChange={(e) => setDuration(e.target.value)}
                        placeholder="1.5"
                        className="w-32 mt-1 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 transition-colors"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 transition-colors">
                        Will use same date/time as note
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer - Fixed */}
          {step === 'review' && (
            <div className="bg-gray-50 dark:bg-gray-700/50 px-4 py-4 sm:px-6 sm:flex sm:flex-row-reverse gap-2 border-t border-gray-200 dark:border-gray-700 flex-shrink-0 transition-colors">
              <Button
                onClick={handleFinalSubmit}
                disabled={loading}
                className="w-full sm:w-auto bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
              >
                {loading ? 'Saving...' : 'Save Note'}
              </Button>
              <Button
                onClick={handleDiscard}
                variant="outline"
                className="w-full sm:w-auto border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                disabled={loading}
              >
                Discard
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateNoteModal;

